/**
 * Hybrid rerun person payload helpers.
 *
 * Contract and pitfalls: see `docs/HYBRID_RERUN.md`.
 *
 * - Subject field mapping: `personInfoApiFromWizardSteps`, `personInfoApiFromPersonFormState`
 * - PATCH person payload: `buildHybridRerunPersonPayload` (staging `person_uuid` only)
 * - Do not send parent `person_uuid` as `person.person_uuid` — use `parentPersonUuid` guard
 */
import { Degrees, Jobs, NameType, PersonInfoApi } from "./Person";
import {
  AddressesState,
  DegreesState,
  EmailState,
  JobState,
  NameState,
  PersonFormState,
  PhonesState,
} from "./Reports";
import { formatDateForEndpoint } from "../utils";

/** Local UI field shapes used when reducer state is not yet synced from prefill. */
export type PersonFormLocalFields = {
  birthDate: string;
  aliases?: Array<{
    firstName: string;
    middleName: string;
    lastName: string;
    name_type: NameType;
  }>;
  jobs?: Array<{
    title: string;
    company: string;
    startDate?: string;
    endDate?: string;
    country: string;
  }>;
  emails?: Array<{ email: string }>;
  phones?: Array<{ phone: string }>;
  addresses?: Array<{
    street: string;
    city: string;
    state: string;
    zip?: string;
    country: string;
  }>;
  education?: Array<{
    degree: string;
    institution: string;
    startDate?: string;
    endDate?: string;
    country: string;
  }>;
};

export type WizardPersonStepInput = {
  firstName: string;
  middleName: string;
  lastName: string;
  aliases: Array<{
    firstName: string;
    middleName: string;
    lastName: string;
    name_type: NameType;
  }>;
  emails: string[];
  phones: string[];
  dateOfBirth: string;
  jobs: Array<{
    title: string;
    company: string;
    startDate?: string;
    endDate?: string;
    country: string;
  }>;
  addresses: Array<{
    street: string;
    city: string;
    state: string;
    zip?: string;
    country: string;
  }>;
  degrees: Array<{
    degree: string;
    institution: string;
    startDate?: string;
    endDate?: string;
    country: string;
  }>;
};

type PersonFormPersonFields = Pick<
  PersonFormState,
  | "birth_date"
  | "names"
  | "jobs"
  | "addresses"
  | "degrees"
  | "phones"
  | "emails"
>;

function normalizePhoneDigits(raw: string): string {
  return raw.replace(/[^0-9]/g, "");
}

function trimOrUndefined(value: string | undefined | null): string | undefined {
  const trimmed = (value ?? "").trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

function jobStateToJobs(job: JobState): Jobs {
  return {
    title: job.title,
    employer: job.employer,
    country: job.country,
    start_date:
      (job.start_date ? formatDateForEndpoint(job.start_date) : undefined) ??
      "",
    end_date:
      (job.end_date ? formatDateForEndpoint(job.end_date) : undefined) ?? "",
  };
}

function degreesStateToDegrees(degree: DegreesState): Degrees {
  return {
    name: degree.name,
    educator: degree.educator,
    country: degree.country,
    start_date:
      (degree.start_date
        ? formatDateForEndpoint(degree.start_date)
        : undefined) ?? "",
    end_date:
      (degree.end_date ? formatDateForEndpoint(degree.end_date) : undefined) ??
      "",
  };
}

function normalizeNames(names: PersonInfoApi["names"]): PersonInfoApi["names"] {
  return [...names]
    .map((name) => ({
      first_name: name.first_name.trim(),
      middle_name: trimOrUndefined(name.middle_name) ?? "",
      last_name: name.last_name.trim(),
      name_type: name.name_type,
    }))
    .sort((a, b) =>
      `${a.name_type}:${a.first_name}:${a.middle_name}:${a.last_name}`.localeCompare(
        `${b.name_type}:${b.first_name}:${b.middle_name}:${b.last_name}`
      )
    );
}

function normalizeJobs(jobs: PersonInfoApi["jobs"]): PersonInfoApi["jobs"] {
  return [...jobs]
    .map((job) => ({
      title: job.title.trim(),
      employer: job.employer.trim(),
      country: (job.country ?? "").trim(),
      start_date:
        trimOrUndefined(
          job.start_date ? formatDateForEndpoint(job.start_date) : undefined
        ) ?? "",
      end_date:
        trimOrUndefined(
          job.end_date ? formatDateForEndpoint(job.end_date) : undefined
        ) ?? "",
    }))
    .sort((a, b) =>
      `${a.title}:${a.employer}:${a.start_date}:${a.end_date}`.localeCompare(
        `${b.title}:${b.employer}:${b.start_date}:${b.end_date}`
      )
    );
}

function normalizeAddresses(
  addresses: PersonInfoApi["addresses"]
): PersonInfoApi["addresses"] {
  return [...addresses]
    .map((address) => ({
      line1: (address.line1 ?? "").trim(),
      line2: (address.line2 ?? "").trim(),
      city: (address.city ?? "").trim(),
      state: (address.state ?? "").trim(),
      zip: trimOrUndefined(address.zip),
      country: (address.country ?? "").trim(),
      start_date: trimOrUndefined(
        address.start_date
          ? formatDateForEndpoint(address.start_date)
          : undefined
      ),
      end_date: trimOrUndefined(
        address.end_date ? formatDateForEndpoint(address.end_date) : undefined
      ),
    }))
    .sort((a, b) =>
      `${a.line1}:${a.city}:${a.state}:${a.zip ?? ""}`.localeCompare(
        `${b.line1}:${b.city}:${b.state}:${b.zip ?? ""}`
      )
    );
}

function normalizeDegrees(
  degrees: PersonInfoApi["degrees"]
): PersonInfoApi["degrees"] {
  return [...degrees]
    .map((degree) => ({
      name: degree.name.trim(),
      educator: degree.educator.trim(),
      country: (degree.country ?? "").trim(),
      start_date:
        trimOrUndefined(
          degree.start_date
            ? formatDateForEndpoint(degree.start_date)
            : undefined
        ) ?? "",
      end_date:
        trimOrUndefined(
          degree.end_date ? formatDateForEndpoint(degree.end_date) : undefined
        ) ?? "",
    }))
    .sort((a, b) =>
      `${a.name}:${a.educator}:${a.start_date}`.localeCompare(
        `${b.name}:${b.educator}:${b.start_date}`
      )
    );
}

function normalizePhones(
  phones: PersonInfoApi["phones"]
): PersonInfoApi["phones"] {
  return [...phones]
    .map((phone) => ({
      raw_number: normalizePhoneDigits(phone.raw_number),
    }))
    .filter((phone) => phone.raw_number.length > 0)
    .sort((a, b) => a.raw_number.localeCompare(b.raw_number));
}

function normalizeEmails(
  emails: PersonInfoApi["emails"]
): PersonInfoApi["emails"] {
  return [...emails]
    .map((email) => ({ address: email.address.trim() }))
    .filter((email) => email.address.length > 0)
    .sort((a, b) => a.address.localeCompare(b.address));
}

function normalizeIdentifications(
  identifications: PersonInfoApi["identifications"]
): PersonInfoApi["identifications"] {
  return [...identifications]
    .map((id) => ({
      unique_id: id.unique_id.trim(),
      description: id.description.trim(),
    }))
    .sort((a, b) => a.unique_id.localeCompare(b.unique_id));
}

/** Canonical shape for equality checks (ignores modified_by / enriched_by_item). */
export function normalizePersonInfoForCompare(
  api: PersonInfoApi
): PersonInfoApi {
  const birth_date = trimOrUndefined(
    api.birth_date ? formatDateForEndpoint(api.birth_date) : undefined
  );
  return {
    names: normalizeNames(api.names ?? []),
    jobs: normalizeJobs(api.jobs ?? []),
    addresses: normalizeAddresses(api.addresses ?? []),
    degrees: normalizeDegrees(api.degrees ?? []),
    phones: normalizePhones(api.phones ?? []),
    emails: normalizeEmails(api.emails ?? []),
    identifications: normalizeIdentifications(api.identifications ?? []),
    ...(birth_date !== undefined ? { birth_date } : {}),
  };
}

export function personInfoApiEquals(
  a: PersonInfoApi,
  b: PersonInfoApi
): boolean {
  return (
    JSON.stringify(normalizePersonInfoForCompare(a)) ===
    JSON.stringify(normalizePersonInfoForCompare(b))
  );
}

function namesFromLocalAliases(
  aliases: PersonFormLocalFields["aliases"]
): NameState[] {
  return (aliases ?? []).map((alias) => ({
    first_name: alias.firstName.trim(),
    middle_name: alias.middleName.trim(),
    last_name: alias.lastName.trim(),
    name_type: alias.name_type,
  }));
}

function jobsFromLocal(jobs: PersonFormLocalFields["jobs"]): JobState[] {
  return (jobs ?? []).map((job) => ({
    country: job.country,
    employer: job.company,
    start_date: job.startDate === "" ? undefined : job.startDate,
    end_date: job.endDate === "" ? undefined : job.endDate,
    title: job.title,
  }));
}

function addressesFromLocal(
  addresses: PersonFormLocalFields["addresses"]
): AddressesState[] {
  return (addresses ?? []).map((address) => ({
    city: address.city,
    country: address.country,
    line1: address.street,
    line2: "",
    state: address.state,
    zip: address.zip === "" ? undefined : address.zip,
  }));
}

function degreesFromLocal(
  education: PersonFormLocalFields["education"]
): DegreesState[] {
  return (education ?? []).map((item) => ({
    country: item.country,
    educator: item.institution,
    name: item.degree,
    start_date: item.startDate === "" ? undefined : item.startDate,
    end_date: item.endDate === "" ? undefined : item.endDate,
  }));
}

function phonesFromLocal(
  phones: PersonFormLocalFields["phones"]
): PhonesState[] {
  return (phones ?? []).map((phone) => ({
    raw_number: phone.phone,
  }));
}

function emailsFromLocal(
  emails: PersonFormLocalFields["emails"]
): EmailState[] {
  return (emails ?? []).map((email) => ({
    address: email.email,
  }));
}

function resolveNames(
  state: PersonFormPersonFields,
  local?: PersonFormLocalFields
): NameState[] {
  if (state.names.length > 0) {
    return state.names;
  }
  return namesFromLocalAliases(local?.aliases);
}

function resolveJobs(
  state: PersonFormPersonFields,
  local?: PersonFormLocalFields
) {
  return state.jobs.length > 0 ? state.jobs : jobsFromLocal(local?.jobs);
}

function resolveAddresses(
  state: PersonFormPersonFields,
  local?: PersonFormLocalFields
) {
  return state.addresses.length > 0
    ? state.addresses
    : addressesFromLocal(local?.addresses);
}

function resolveDegrees(
  state: PersonFormPersonFields,
  local?: PersonFormLocalFields
) {
  return state.degrees.length > 0
    ? state.degrees
    : degreesFromLocal(local?.education);
}

function resolvePhones(
  state: PersonFormPersonFields,
  local?: PersonFormLocalFields
) {
  return state.phones.length > 0
    ? state.phones
    : phonesFromLocal(local?.phones);
}

function resolveEmails(
  state: PersonFormPersonFields,
  local?: PersonFormLocalFields
) {
  return state.emails.length > 0
    ? state.emails
    : emailsFromLocal(local?.emails);
}

function resolveBirthDate(
  state: PersonFormPersonFields,
  local?: PersonFormLocalFields
): string {
  if (state.birth_date.trim().length > 0) {
    return state.birth_date;
  }
  return local?.birthDate ?? "";
}

/** Build PersonInfoApi from PersonForm / PersonFormv2 state (mirrors createPerson person_info). */
export function personInfoApiFromPersonFormState(
  state: PersonFormPersonFields,
  local?: PersonFormLocalFields
): PersonInfoApi {
  const names = resolveNames(state, local).map((name) => ({
    first_name: name.first_name.trim(),
    last_name: name.last_name.trim(),
    middle_name: name.middle_name.trim(),
    name_type: name.name_type,
  }));
  const birthDate = resolveBirthDate(state, local);
  return {
    names,
    jobs: resolveJobs(state, local).map(jobStateToJobs),
    addresses: resolveAddresses(state, local),
    birth_date: formatDateForEndpoint(birthDate),
    degrees: resolveDegrees(state, local).map(degreesStateToDegrees),
    phones: resolvePhones(state, local).map((p) => ({
      raw_number: p.raw_number.replace(/[^0-9]/g, ""),
    })),
    emails: resolveEmails(state, local),
    identifications: [],
  };
}

/** Build PersonInfoApi from reports wizard step data (mirrors createPerson2025 person). */
export function personInfoApiFromWizardSteps(
  input: WizardPersonStepInput
): PersonInfoApi {
  const names = [
    {
      first_name: input.firstName.trim(),
      last_name: input.lastName.trim(),
      middle_name: input.middleName.trim(),
      name_type: NameType.MAIN,
    },
    ...input.aliases.map((name) => ({
      first_name: name.firstName.trim(),
      last_name: name.lastName.trim(),
      middle_name: name.middleName.trim(),
      name_type: name.name_type,
    })),
  ];
  const jobs = input.jobs.map((job) =>
    jobStateToJobs({
      title: job.title,
      employer: job.company,
      country: job.country ?? "",
      start_date: job.startDate ?? undefined,
      end_date: job.endDate ?? undefined,
    })
  );
  const addresses: AddressesState[] = input.addresses.map((address) => ({
    line1: address.street,
    line2: "",
    city: address.city,
    state: address.state,
    zip: address.zip,
    country: address.country,
  }));
  const degrees = input.degrees.map((degree) =>
    degreesStateToDegrees({
      name: degree.degree,
      educator: degree.institution,
      country: degree.country ?? "",
      start_date: degree.startDate ?? undefined,
      end_date: degree.endDate ?? undefined,
    })
  );

  return {
    names,
    jobs,
    addresses,
    birth_date: formatDateForEndpoint(input.dateOfBirth),
    degrees,
    phones: input.phones.map((p) => ({
      raw_number: normalizePhoneDigits(p),
    })),
    emails: input.emails.map((email) => ({
      address: email,
    })),
    identifications: [],
  };
}

/** Build person payload for hybrid rerun PATCH (always creates a new person server-side).

See `docs/HYBRID_RERUN.md` for the three UUID concepts (parent subject, staging, new identity).

When `personUuid` is a wizard staging person from `/person/create/empty` and differs from
`parentPersonUuid`, it is sent as `person_uuid` so S3 attachments remain on the report person.
Never pass the parent subject UUID as `personUuid`.
*/
export function buildHybridRerunPersonPayload(input: {
  current: PersonInfoApi;
  modifiedBy?: string;
  personUuid?: string;
  parentPersonUuid?: string;
}): PersonInfoApi {
  const person: PersonInfoApi = input.modifiedBy
    ? { ...input.current, modified_by: input.modifiedBy }
    : { ...input.current };
  const stagingUuid = input.personUuid?.trim();
  const parentUuid = input.parentPersonUuid?.trim();
  if (stagingUuid && stagingUuid !== parentUuid) {
    person.person_uuid = stagingUuid;
  }
  return person;
}
